/**
 * Account Menu - Consolidated user account dropdown
 * Groups Property selector, Logout, and DEV badge
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useSession } from '../../contexts/SessionContext.jsx';
import Popover from '../shared/Popover.jsx';
import { Cog, LogOut, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';

export default function AccountMenu({ owner, onLogout }) {
  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';
  
  const { theme } = useTheme();
  const { effectiveMode, selectedListingId, selectedPropertyId, setSelectedPropertyId, setSelectedListingId, selectedProperty, availableProperties, propertiesLoading, propertiesError } = useSession();
  
  // PHASE D1: Event logging helper (debug=1 only)
  const logEvt = (phase, e, meta) => {
    if (!debug) return;
    console.log('[OWNER_EVT][ROW][CAPTURE]', {
      phase,
      type: e.type,
      listingId: meta.listingId,
      name: meta.name,
      target: e.target?.tagName,
      currentTarget: e.currentTarget?.tagName,
      defaultPrevented: e.defaultPrevented,
      isTrusted: e.isTrusted,
      timestamp: new Date().toISOString(),
    });
  };
  
  // PHASE D1: Proof log that instrumentation is mounted
  useEffect(() => {
    if (debug) {
      console.log('[OWNER_EVT] AccountMenu instrumentation enabled');
    }
  }, [debug]);
  
  // PHASE D1: Document-level CAPTURE listeners (debug=1 only)
  useEffect(() => {
    if (!debug) return;
    
    const logDocEvent = (eventType, event) => {
      const target = event.target;
      const path = event.composedPath ? event.composedPath().slice(0, 6) : [];
      console.log('[OWNER_EVT][DOC][CAPTURE]', {
        type: eventType,
        target: target?.tagName,
        id: target?.id || null,
        className: target?.className || null,
        testId: target?.getAttribute('data-testid') || null,
        path: path.map(n => n?.tagName ? `${n.tagName}${n.id ? '#' + n.id : ''}` : String(n)).slice(0, 6),
        defaultPrevented: event.defaultPrevented,
        isTrusted: event.isTrusted,
        timestamp: new Date().toISOString(),
      });
    };
    
    const handlePointerDown = (e) => logDocEvent('pointerdown', e);
    const handleMouseDown = (e) => logDocEvent('mousedown', e);
    const handleClick = (e) => logDocEvent('click', e);
    
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('click', handleClick, true);
    
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('click', handleClick, true);
    };
  }, [debug]);
  
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [propertySearchQuery, setPropertySearchQuery] = useState('');
  const menuRef = useRef(null);
  const menuItemsRef = useRef([]);
  const searchInputRef = useRef(null);
  const propertyRowRefs = useRef({}); // Map of listingId -> ref

  const handlePropertyChange = (property) => {
    // Use listing_id (canonical identifier) - property must be an object
    if (!property || typeof property !== 'object') {
      if (debug) {
        console.warn('[OWNER_SELECT][AccountMenu] handlePropertyChange called with invalid property:', property);
      }
      return;
    }
    
    const listingId = Number(property.listing_id);
    if (!Number.isFinite(listingId) || listingId <= 0) {
      if (debug) {
        console.warn('[OWNER_SELECT][AccountMenu] Invalid listing_id in property:', property);
      }
      return;
    }
    
    // Only log in debug mode
    if (debug) {
      console.log('[OWNER_SELECT][AccountMenu] handlePropertyChange:', {
        propertyListingId: listingId,
        propertyName: property.name || property.property_name,
        previousListingId: selectedListingId,
      });
    }
    
    // Use setSelectedListingId directly (canonical setter) with source tracking
    if (setSelectedListingId) {
      setSelectedListingId(listingId, 'user_click');
      if (debug) {
        console.log('[OWNER_SELECT] [AccountMenu] setSelectedListingId called (AFTER set):', {
          oldValue: selectedListingId,
          newValue: listingId,
          source: 'user_click',
          timestamp: new Date().toISOString(),
        });
      }
    } else {
      // Fallback to backward compatibility layer
      setSelectedPropertyId(String(listingId));
      if (debug) {
        console.log('[OWNER_SELECT] [AccountMenu] setSelectedPropertyId called (fallback):', String(listingId));
      }
    }
    
    setIsExpanded(false);
    setPropertySearchQuery(''); // Clear search when closing
    // Data will refresh automatically via useEffect in pages
  };

  // Debounced search query state
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(propertySearchQuery);
    }, 150); // 150ms debounce
    
    return () => clearTimeout(timer);
  }, [propertySearchQuery]);
  
  // Filter properties by search query (debounced)
  // Search across: display name, full name, CLC short name, city, region, country
  const filteredProperties = useMemo(() => {
    if (!debouncedSearchQuery) return availableProperties;
    const query = debouncedSearchQuery.toLowerCase().trim();
    return availableProperties.filter(property => {
      // Build combined searchable string (normalized: lowercase, trimmed)
      const displayName = (property.display_name || '').toLowerCase().trim();
      const fullName = (property.name || '').toLowerCase().trim();
      const shortName = (property.clc_short_name || '').toLowerCase().trim();
      const city = (property.city || '').toLowerCase().trim();
      const region = (property.region || '').toLowerCase().trim();
      const country = (property.country || '').toLowerCase().trim();
      
      // Combine all searchable fields into one string (collapse whitespace)
      const searchableText = [displayName, fullName, shortName, city, region, country]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' '); // Normalize whitespace
      
      return searchableText.includes(query);
    });
  }, [availableProperties, debouncedSearchQuery]);
  
  // Get property location string
  const getPropertyLocation = useCallback((property) => {
    const parts = [];
    if (property.city) parts.push(property.city);
    if (property.region) parts.push(property.region);
    if (property.country) parts.push(property.country);
    return parts.length > 0 ? parts.join(', ') : null;
  }, []);
  
  // Get property thumbnail URL (CLC thumb > hostaway thumbnail)
  // Get property thumbnail (thumb_url only, curated-only contract)
  // NEVER use Hostaway thumbnail_url/image_url as fallback
  const getPropertyThumbnail = useCallback((property) => {
    return property.thumb_url || null;
  }, []);
  
  // Get property display name (display_name > clc_short_name > property_name/name)
  // NEVER use contact_name as fallback
  const getPropertyShortName = useCallback((property) => {
    return property.display_name || property.clc_short_name || property.name || property.property_name || 'Unnamed Property';
  }, []);

  // Reset expanded state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setPropertySearchQuery(''); // Clear search when closing
    }
  }, [isOpen]);

  // Focus search input when expanded
  useEffect(() => {
    if (isExpanded && searchInputRef.current) {
      if (debug) {
        console.log('[OWNER_SELECT][UI] Property list expanded', {
          propertyCount: filteredProperties.length,
          currentSelectedListingId: selectedListingId,
          timestamp: new Date().toISOString(),
        });
      }
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isExpanded, filteredProperties.length, selectedListingId, debug]);

  // Focus the first account action only when there is no property list to search.
  useEffect(() => {
    if (isOpen && !isExpanded && menuItemsRef.current.length > 0) {
      setTimeout(() => {
        const firstClickable = menuItemsRef.current.find(el => el);
        firstClickable?.focus();
      }, 0);
    }
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  const menuItems = [
    {
      id: 'property',
      label: selectedProperty ? getPropertyShortName(selectedProperty) : 'Property',
      icon: '', // Icon removed - thumbnail shown separately in picker
      onClick: null,
      show: true,
      nonClickable: true,
    },
    {
      id: 'separator-1',
      label: '',
      icon: '',
      onClick: null,
      show: true,
      separator: true,
    },
    {
      id: 'dev',
      label: 'DEV Environment',
      icon: 'cog',
      onClick: null,
      show: effectiveMode === 'admin' || effectiveMode === 'owner_view',
      badge: 'DEV',
      nonClickable: true,
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: 'arrow-right-on-rectangle',
      onClick: handleLogout,
      show: !!owner && !!onLogout,
      separator: true,
    },
  ].filter(item => item.show);

  return (
    <>
      <Popover
        offset={10}
        trigger={({ ref, ...props }) => (
          <button
            ref={(el) => {
              menuRef.current = el;
              if (typeof ref === 'function') {
                ref(el);
              } else if (ref) {
                ref.current = el;
              }
            }}
            onClick={() => {
              setIsOpen(!isOpen);
              if (!isOpen && availableProperties.length > 1) setIsExpanded(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(!isOpen);
                if (!isOpen && availableProperties.length > 1) setIsExpanded(true);
              }
            }}
            style={{
              ...styles.triggerButton,
              /* Phase 9: Use shared header control tokens */
              backgroundColor: 'var(--header-control-bg, rgba(255, 255, 255, 0.12))',
              color: 'var(--header-control-text, rgba(255, 255, 255, 1.0))',
              borderColor: 'var(--header-control-border, rgba(255, 255, 255, 0.5))',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--header-control-bg-hover, rgba(255, 255, 255, 0.22))';
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--header-control-bg, rgba(255, 255, 255, 0.12))';
              e.currentTarget.style.opacity = '1';
            }}
            aria-label="Account menu"
            aria-expanded={isOpen}
            aria-haspopup="true"
            {...props}
          >
            <div style={styles.avatar}>
              {(owner?.name || owner?.display_name || owner?.email || 'O').charAt(0).toUpperCase()}
            </div>
            {isOpen ? (
              <ChevronUp size={16} style={{ opacity: 0.7, flexShrink: 0 }} />
            ) : (
              <ChevronDown size={16} style={{ opacity: 0.7, flexShrink: 0 }} />
            )}
          </button>
        )}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom-end"
      >
        <div
          style={{
            ...styles.dropdown,
            /* Phase 6: High-contrast styling - solid white background, visible border */
            backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : '#FFFFFF', // Phase 6: Pure white for high contrast
            borderColor: theme === 'dark' ? 'var(--color-border-medium-dark)' : 'rgba(0, 0, 0, 0.12)', // Phase 6: More visible border
            boxShadow: theme === 'dark'
              ? '0 8px 24px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)'
              : '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)', // Phase 6: Stronger shadow
            // Dynamic max height - smaller when collapsed, larger when expanded
            maxHeight: isExpanded ? '580px' : '180px', // Compact when collapsed, full height when expanded
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            /* Phase 6: Ensure visibility */
            opacity: 1,
            visibility: 'visible',
            minHeight: isExpanded ? '100px' : 'auto', // Compact when collapsed
          }}
          role="menu"
        >
          {/* Property Region (scrollable) */}
          <div style={{
            ...styles.propertyRegion,
            backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary, #fafafa)',
            borderBottom: `1px solid ${theme === 'dark' ? 'var(--color-border-light-dark)' : 'rgba(0, 0, 0, 0.06)'}`,
            padding: '6px 6px 8px 6px', // Small inset so tint is visible around rows
          }}>
            {/* Current Property Display (non-clickable) */}
            {menuItems.filter(item => item.id === 'property').map((item, index) => (
              <div key={item.id}>
                <div
                  style={{
                    ...styles.menuItem,
                    ...styles.menuItemNonClickable,
                    ...(selectedProperty ? styles.propertyHeader : {}),
                    color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                  }}
                >
                  <span>{item.label}</span>
                </div>
              </div>
            ))}

            {/* Search input - always visible */}
            <div style={{ 
              padding: '10px 12px 8px 12px', 
              backgroundColor: 'transparent',
            }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search properties..."
                value={propertySearchQuery}
                onChange={(e) => {
                  setPropertySearchQuery(e.target.value);
                  setIsExpanded(true); // Expand when user starts typing
                }}
                onFocus={() => setIsExpanded(true)} // Expand when focused
                onClick={() => setIsExpanded(true)} // Expand when clicked
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    if (isExpanded) {
                      setIsExpanded(false);
                      e.stopPropagation(); // Don't close dropdown, just collapse
                    }
                  }
                }}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-sm)',
                  border: `1px solid ${theme === 'dark' ? 'var(--color-border-light-dark)' : 'var(--color-border-light)'}`,
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : '#FFFFFF',
                  color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                  outline: 'none',
                }}
              />
            </div>

            {/* Property list - only shown when expanded */}
            {isExpanded && (
              <div
                style={{
                  ...styles.propertyCardContainer,
                  backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary, #fafafa)',
                  border: `1px solid ${theme === 'dark' ? 'var(--color-border-light-dark)' : 'rgba(0, 0, 0, 0.08)'}`,
                  borderRadius: '12px',
                  padding: '8px 10px',
                  boxShadow: `0 1px 0 ${theme === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.04)'}`,
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  transition: 'max-height 0.2s ease, opacity 0.2s ease',
                }}
              >

                {/* Error state */}
                {propertiesError && (
                  <div style={{ padding: 'var(--spacing-3)', color: theme === 'dark' ? 'var(--color-status-error-text-dark)' : 'var(--color-status-error-text)', fontSize: 'var(--font-size-sm)', borderBottom: `1px solid ${theme === 'dark' ? 'var(--color-border-light-dark)' : 'var(--color-border-light)'}` }}>
                    Unable to load properties from server.
                  </div>
                )}

                {/* Loading state */}
                {propertiesLoading && !propertiesError && (
                  <div style={{ padding: 'var(--spacing-3)', color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                    Loading properties...
                  </div>
                )}

                {/* Empty state */}
                {!propertiesLoading && !propertiesError && filteredProperties.length === 0 && (
                  <div style={{ padding: 'var(--spacing-3)', color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)' }}>
                    {propertySearchQuery ? 'No properties match your search' : 'No properties available'}
                  </div>
                )}

                {/* Properties list */}
                {!propertiesLoading && !propertiesError && filteredProperties.length > 0 && (
                  <div style={styles.propertyListContainer}>
                        {filteredProperties.map((property, index) => {
                        // Determine if this property is selected (use listing_id as canonical identifier)
                        const currentId = Number(selectedListingId ?? selectedProperty?.listing_id ?? selectedPropertyId ?? null);
                        const isSelected = Number(property.listing_id) === currentId;
                        
                        if (debug && isExpanded) {
                          console.log('[OWNER_SELECT][AccountMenu] Rendering property row:', {
                            index,
                            name: property.name,
                            listing_id: property.listing_id,
                            propertyId: property.id,
                            isSelected,
                            currentSelectedListingId: selectedListingId,
                            currentSelectedPropertyId: selectedPropertyId,
                            currentId,
                          });
                        }
                        
                        const listingIdNum = Number(property.listing_id);
                        const propertyName = property.name || property.property_name;
                        const meta = { listingId: listingIdNum, name: propertyName };
                        
                        // PHASE D4: Handle selection on pointerdown capture (bypasses canceled click)
                        const handlePointerDownCapture = (e) => {
                          // Log event
                          logEvt('pointerdown_capture', e, meta);
                          
                          // Guardrails: only primary pointer button
                          if (e.button !== undefined && e.button !== 0) return; // Right/middle button
                          if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return; // Modified clicks
                          
                          // Trigger selection
                          if (debug) {
                            console.log('[OWNER_EVT][ROW][PD_CAPTURE] Selection triggered:', {
                              listingId: listingIdNum,
                              name: propertyName,
                              timestamp: Date.now(),
                            });
                          }
                          
                          handlePropertyChange(property);
                        };
                        
                        // PHASE D4: Keyboard support
                        const handleKeyDown = (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (debug) {
                              console.log('[OWNER_EVT][ROW][KEYDOWN] Selection triggered:', {
                                listingId: listingIdNum,
                                name: propertyName,
                                key: e.key,
                                timestamp: Date.now(),
                              });
                            }
                            handlePropertyChange(property);
                          }
                        };
                        
                        return (
                        <button
                          key={property.ui_key || property.listing_id || property.id}
                          data-testid={`owner-property-row-${listingIdNum}`}
                          data-listing-id={listingIdNum}
                          data-property-name={propertyName}
                          onPointerDownCapture={handlePointerDownCapture}
                          onMouseDownCapture={(e) => logEvt('mousedown_capture', e, meta)}
                          onMouseUpCapture={(e) => logEvt('mouseup_capture', e, meta)}
                          onKeyDown={handleKeyDown}
                          onClickCapture={(e) => {
                            logEvt('click_capture', e, meta);
                            // Keep old log for compatibility
                            if (debug) {
                              console.log('[OWNER_SELECT][UI] ROW CLICK CAPTURE:', {
                                listingId: listingIdNum,
                                name: propertyName,
                                target: e.target?.tagName,
                                currentTarget: e.currentTarget?.tagName,
                                timestamp: new Date().toISOString(),
                              });
                            }
                          }}
                          onClick={(e) => {
                            // PHASE D4: Keep onClick as fallback
                            if (debug) {
                              console.log('[OWNER_EVT][ROW][CLICK] Fallback onClick:', {
                                listingId: listingIdNum,
                                name: propertyName,
                                timestamp: Date.now(),
                              });
                            }
                            handlePropertyChange(property);
                            // Clickability probe (debug only)
                            if (debug) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const centerX = rect.left + rect.width / 2;
                              const centerY = rect.top + rect.height / 2;
                              const elementAtPoint = document.elementFromPoint(centerX, centerY);
                              const computedStyle = window.getComputedStyle(e.currentTarget);
                              
                              console.log('[OWNER_SELECT][UI] Property click (DIAGNOSTICS):', {
                                name: propertyName,
                                listing_id: property.listing_id,
                                currentSelectedListingId: selectedListingId,
                                eventTarget: {
                                  tagName: e.target.tagName,
                                  className: e.target.className || '(no class)',
                                },
                                eventCurrentTarget: {
                                  tagName: e.currentTarget.tagName,
                                  className: e.currentTarget.className || '(no class)',
                                },
                                computedStyle: {
                                  pointerEvents: computedStyle.pointerEvents,
                                  zIndex: computedStyle.zIndex,
                                  position: computedStyle.position,
                                },
                                boundingRect: {
                                  left: rect.left,
                                  top: rect.top,
                                  width: rect.width,
                                  height: rect.height,
                                },
                                elementFromPoint: {
                                  tagName: elementAtPoint?.tagName || 'null',
                                  className: elementAtPoint?.className || '(no class)',
                                  isCurrentTarget: elementAtPoint === e.currentTarget,
                                },
                                timestamp: new Date().toISOString(),
                              });
                            }
                            
                            // Log click (standard)
                            if (debug) {
                              console.log('[OWNER_SELECT][UI] Property click (BEFORE handlePropertyChange):', {
                                name: propertyName,
                                listing_id: property.listing_id,
                                currentSelectedListingId: selectedListingId,
                                timestamp: new Date().toISOString(),
                              });
                            }
                            
                            handlePropertyChange(property);
                          }}
                          style={{
                            ...styles.propertyOption,
                            backgroundColor: isSelected
                              ? (theme === 'dark' ? 'rgba(0, 122, 255, 0.18)' : 'rgba(0, 122, 255, 0.12)')
                              : 'transparent',
                            color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                            borderBottomColor: theme === 'dark' ? 'var(--color-border-light-dark)' : 'rgba(0, 0, 0, 0.06)',
                            borderRadius: '8px', // Rounded corners for selectable items within card
                            borderLeft: isSelected ? `2px solid ${theme === 'dark' ? 'rgba(0, 122, 255, 0.7)' : 'rgba(0, 122, 255, 0.7)'}` : '2px solid transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
                              e.currentTarget.style.borderLeft = `2px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderLeft = '2px solid transparent';
                            }
                          }}
                        >
                          {/* Thumbnail */}
                          <div style={styles.propertyThumbnail}>
                            {getPropertyThumbnail(property) ? (
                              <img
                                src={getPropertyThumbnail(property)}
                                alt={property.name || 'Property'}
                                style={styles.propertyThumbnailImage}
                                onError={(e) => {
                                  // Fallback to placeholder on error
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div style={{
                              ...styles.propertyThumbnailPlaceholder,
                              display: getPropertyThumbnail(property) ? 'none' : 'flex',
                            }}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>
                          
                          {/* Property Info */}
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                            <div style={{ 
                              fontWeight: 'var(--font-weight-semibold)', // All property names semibold for hierarchy
                              fontSize: 'var(--font-size-sm)',
                              lineHeight: '1.4',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {getPropertyShortName(property)}
                            </div>
                          </div>
                          
                          {/* Selected Indicator */}
                          {isSelected && (
                            <span style={{ 
                              color: theme === 'dark' ? 'var(--color-brand-primary-dark)' : 'var(--color-brand-primary)', 
                              fontSize: 'var(--font-size-base)',
                              flexShrink: 0,
                              marginLeft: 'var(--spacing-2)',
                            }}>✓</span>
                          )}
                        </button>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions Region (pinned at bottom) */}
          <div style={{
            ...styles.actionsRegion,
            backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary, #ffffff)',
            borderTop: `1px solid ${theme === 'dark' ? 'var(--color-border-light-dark)' : 'rgba(0, 0, 0, 0.06)'}`,
          }}>
            {menuItems.filter(item => item.id !== 'property' && item.id !== 'separator-1' && item.id !== 'dev').map((item, index) => {
              // Find original index for refs
              const originalIndex = menuItems.findIndex(originalItem => originalItem.id === item.id);
              return (
              <div key={item.id}>
                {item.separator && (
                  <div style={{
                    ...styles.separator,
                    borderColor: theme === 'dark' ? 'var(--color-border-light-dark)' : 'var(--color-border-light)',
                  }} />
                )}
                {item.nonClickable ? (
                  <div
                    style={{
                      ...styles.menuItem,
                      ...styles.menuItemNonClickable,
                      color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                    }}
                  >
                    {item.icon && (
                      <div style={{
                        ...styles.menuItemIcon,
                        color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'rgba(0, 0, 0, 0.55)',
                      }}>
                        {item.icon === 'cog' && <Cog size={18} />}
                        {item.icon === 'arrow-right-on-rectangle' && <LogOut size={18} />}
                      </div>
                    )}
                    <span>{item.label}</span>
                    {item.badge && (
                      <span style={styles.badge}>{item.badge}</span>
                    )}
                  </div>
                ) : (
                  <button
                    ref={(el) => {
                      if (el) menuItemsRef.current[originalIndex] = el;
                    }}
                    onClick={item.onClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        item.onClick();
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const actionItems = menuItems.filter(i => i.id !== 'property' && i.id !== 'separator-1' && i.id !== 'dev');
                        const currentActionIndex = actionItems.findIndex(actionItem => actionItem.id === item.id);
                        const nextActionItem = actionItems[currentActionIndex + 1];
                        if (nextActionItem) {
                          const nextOriginalIndex = menuItems.findIndex(originalItem => originalItem.id === nextActionItem.id);
                          const next = menuItemsRef.current[nextOriginalIndex];
                          if (next) next.focus();
                        }
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const actionItems = menuItems.filter(i => i.id !== 'property' && i.id !== 'separator-1' && i.id !== 'dev');
                        const currentActionIndex = actionItems.findIndex(actionItem => actionItem.id === item.id);
                        const prevActionItem = actionItems[currentActionIndex - 1];
                        if (prevActionItem) {
                          const prevOriginalIndex = menuItems.findIndex(originalItem => originalItem.id === prevActionItem.id);
                          const prev = menuItemsRef.current[prevOriginalIndex];
                          if (prev) prev.focus();
                        }
                      }
                    }}
                    style={{
                      ...styles.menuItem,
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme === 'dark' ? 'var(--color-bg-hover-dark)' : 'var(--color-bg-hover)';
                      const iconDiv = e.currentTarget.querySelector('[data-icon-wrapper]');
                      if (iconDiv) {
                        iconDiv.style.color = theme === 'dark' ? 'var(--color-text-primary-dark)' : 'rgba(0, 0, 0, 0.75)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      const iconDiv = e.currentTarget.querySelector('[data-icon-wrapper]');
                      if (iconDiv) {
                        iconDiv.style.color = theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'rgba(0, 0, 0, 0.55)';
                      }
                    }}
                    role="menuitem"
                  >
                    {item.icon && (
                      <div style={{
                        ...styles.menuItemIcon,
                        color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'rgba(0, 0, 0, 0.55)',
                      }} data-icon-wrapper>
                        {item.icon === 'cog' && <Cog size={18} />}
                        {item.icon === 'arrow-right-on-rectangle' && <LogOut size={18} />}
                      </div>
                    )}
                    <span>{item.label}</span>
                    {item.badge && (
                      <span style={styles.badge}>{item.badge}</span>
                    )}
                    {item.hasSubmenu && (
                      <span style={{ marginLeft: 'auto' }}>
                        {item.submenuOpen ? (
                          <ChevronUp size={14} style={{ opacity: 0.6 }} />
                        ) : (
                          <ChevronRight size={14} style={{ opacity: 0.6 }} />
                        )}
                      </span>
                    )}
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      </Popover>

    </>
  );
}

const styles = {
  wrapper: {
    position: 'relative',
    zIndex: 10,
  },
  triggerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    /* Phase 9: Use shared header control tokens for consistency */
    padding: 'var(--header-control-padding, var(--spacing-1) var(--spacing-2))',
    borderRadius: 'var(--header-control-radius, var(--radius-md))',
    border: `1px solid var(--header-control-border, rgba(255, 255, 255, 0.5))`,
    backgroundColor: 'var(--header-control-bg, rgba(255, 255, 255, 0.12))',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-normal)', /* Phase 1.5: Typography alignment - corrected to CLC standard token */
    color: 'var(--header-control-text, rgba(255, 255, 255, 1.0))',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
    whiteSpace: 'nowrap',
    height: 'var(--header-control-height, 36px)',
    opacity: 1, /* Phase 7: Fully opaque */
    minWidth: '80px',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary-base)',
    color: 'var(--color-text-inverse)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    flexShrink: 0,
  },
  chevron: {
    fontSize: 'var(--font-size-xs)',
    opacity: 0.7,
    flexShrink: 0,
  },
  dropdown: {
    /* Phase 6: Fixed positioning - Popover portals to body with position: fixed */
    position: 'relative', // Relative to Popover's fixed container, not absolute
    width: '360px',
    minWidth: '280px', // Phase 6: Ensure minimum width so content doesn't collapse
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(37, 72, 96, 0.12)', // Phase 10: Brand-tinted border for consistency
    zIndex: 1000,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(37, 72, 96, 0.25), 0 2px 8px rgba(0, 0, 0, 0.08)', // Phase 10: Brand-tinted shadow for consistency
    // Phase 11: Increased to accommodate 5 visible properties (was 520px)
    maxHeight: '580px', // Increased from 520px to accommodate larger property list
    display: 'flex',
    flexDirection: 'column',
    /* Phase 6: Ensure dropdown is visible and has solid background */
    backgroundColor: 'var(--color-bg-primary, #ffffff)', // Phase 10: Use token for consistency
    opacity: 1, // Phase 6: Ensure fully opaque
    visibility: 'visible', // Phase 6: Ensure visible
  },
  propertyRegion: {
    flex: '1 1 auto',
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    // Background and border applied inline for theme awareness
  },
  actionsRegion: {
    flex: '0 0 auto',
    backgroundColor: 'var(--color-bg-secondary, #fafafa)',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)',
    padding: '10px 12px', // Phase 6: Adequate padding so items aren't flush to edges
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    textDecoration: 'none',
    transition: 'var(--transition-base)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    /* Phase 6: High-contrast text color - dark on white */
    color: '#1a1a1a', // Phase 6: Dark text for readability on white background
    height: '44px',
    boxSizing: 'border-box',
    minHeight: '44px', // Phase 6: Ensure items have minimum height
  },
  menuItemNonClickable: {
    cursor: 'default',
    opacity: 0.7,
  },
  menuItemIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
  separator: {
    height: '1px',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    margin: '4px 0',
  },
  badge: {
    marginLeft: 'auto',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-inverse)',
    backgroundColor: 'var(--color-status-error-base)',
    padding: '2px 6px',
    borderRadius: 'var(--radius-sm)',
    minWidth: '18px',
    textAlign: 'center',
  },
  propertySubmenu: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-secondary)',
  },
  propertyCardContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    margin: '0',
    // Card styling applied inline for theme awareness
  },
  propertyHeader: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
  },
  propertyListContainer: {
    // Phase 11: Increased to show ~5 properties on desktop (56px per row × 5 = 280px + padding)
    maxHeight: '300px', // Increased from 240px to show ~5 properties comfortably
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  propertyThumbnail: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: 'var(--spacing-2)',
  },
  propertyThumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  propertyThumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.4,
    display: 'flex',
    color: 'var(--color-text-secondary)',
  },
  propertyOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-2)',
    padding: '8px 12px',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'left',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color 0.15s ease',
    borderBottom: '1px solid var(--color-border-light)',
    minHeight: '56px',
    boxSizing: 'border-box',
  },
};
